import React, { useState } from 'react';
import { makeStyles } from '@fluentui/react-components';
import { ArrowLeft24Regular } from '@fluentui/react-icons';
import { signInAdmin } from '../firebase';
import { motion } from 'framer-motion';
import themeImage from '../assets/theme.jpg';

const FONT_FAMILY = "'Cormorant Garamond', serif";

const useStyles = makeStyles({
  loginContainer: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: `url(${themeImage}) center/cover no-repeat fixed, linear-gradient(to bottom right, #fff8e1, #ffe0b2)`,
    padding: '2rem',
    fontFamily: FONT_FAMILY,
  },
  loginCard: {
    background: 'rgba(255, 253, 248, 0.95)',
    padding: '3rem',
    borderRadius: '15px',
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    maxWidth: '400px',
    width: '100%',
    textAlign: 'center',
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    top: '1rem',
    left: '1rem',
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    color: 'var(--primary-color)',
    fontSize: '1rem',
    fontWeight: 600,
    fontFamily: FONT_FAMILY,
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    padding: '0.5rem',
    borderRadius: '6px',
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: 'rgba(12, 59, 94, 0.1)',
      transform: 'translateX(-2px)',
    },
    '&:focus': {
      outline: '2px solid var(--primary-color)',
      outlineOffset: '2px',
    },
  },
  title: {
    color: 'var(--primary-color)',
    fontSize: '2rem',
    marginBottom: '2rem',
    lineHeight: 1,
    fontWeight: 600,
  },
  formField: {
    marginBottom: '1.5rem',
  },
  label: {
    display: 'block',
    marginBottom: '0.5rem',
    fontWeight: 600,
    color: 'var(--primary-color)',
    fontSize: '1rem',
    textAlign: 'left',
  },
  input: {
    width: '100%',
    padding: '0.75rem',
    fontSize: '1rem',
    borderRadius: '8px',
    border: '2px solid #e0e0e0',
    backgroundColor: '#ffffff',
    fontFamily: FONT_FAMILY,
    transition: 'all 0.3s ease',
    '&:focus': {
      outline: 'none',
      border: '2px solid var(--primary-color)',
      boxShadow: '0 0 0 3px rgba(12, 59, 94, 0.1)',
    },
    '&:hover': {
      border: '2px solid #c0c0c0',
    },
  },
  loginButton: {
    width: '100%',
    padding: '0.75rem 2rem',
    fontSize: '1.1rem',
    fontWeight: 600,
    backgroundColor: 'var(--primary-color)',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    cursor: 'pointer',
    fontFamily: FONT_FAMILY,
    transition: 'all 0.3s ease',
    '&:hover:not(:disabled)': {
      backgroundColor: 'var(--primary-hover)',
      transform: 'translateY(-2px)',
      boxShadow: '0 4px 15px rgba(12, 59, 94, 0.2)',
    },
    '&:disabled': {
      backgroundColor: '#d0d0d0',
      color: '#666666',
      cursor: 'not-allowed',
      transform: 'none',
      boxShadow: 'none',
    },
  },
  errorMessage: {
    color: 'var(--error-color)',
    fontSize: '0.9rem',
    marginTop: '1rem',
    padding: '0.75rem',
    backgroundColor: 'rgba(211, 47, 47, 0.1)',
    borderRadius: '8px',
    border: '1px solid var(--error-color)',
  },
  spinner: {
    width: '20px',
    height: '20px',
    border: '2px solid transparent',
    borderTop: '2px solid #ffffff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    display: 'inline-block',
    marginRight: '0.5rem',
  },
});

interface AdminLoginProps {
  onLoginSuccess: () => void;
  onBack?: () => void;
}

const AdminLogin: React.FC<AdminLoginProps> = ({ onLoginSuccess, onBack }) => {
  const classes = useStyles();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      await signInAdmin(email, password);
      onLoginSuccess();
    } catch (err: any) {
      console.error('Login error:', err);
      setError(err.message || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      // Fallback: go to home page
      window.location.href = '/';
    }
  };

  return (
    <div className={classes.loginContainer}>
      <motion.div 
        className={classes.loginCard}
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5, ease: "easeOut" }}
      >
        {/* Back button */}
        <button 
          onClick={handleBack}
          className={classes.backButton}
          type="button"
          title="Back to wedding website"
        >
          <ArrowLeft24Regular style={{ fontSize: '18px' }} />
          Back
        </button>

        <h1 className={classes.title}>Admin Login</h1>
        <p style={{ 
          color: '#6d4c41', 
          marginBottom: '2rem',
          fontSize: '1rem'
        }}>
          Enter your credentials to access the wedding dashboard
        </p>
        
        <form onSubmit={handleSubmit}>
          <div className={classes.formField}>
            <label className={classes.label} htmlFor="email">
              Email Address
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className={classes.input}
              placeholder="admin@example.com"
              disabled={loading}
            />
          </div>

          <div className={classes.formField}>
            <label className={classes.label} htmlFor="password">
              Password
            </label>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className={classes.input}
              placeholder="Enter your password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className={classes.loginButton}
            disabled={loading || !email || !password}
          >
            {loading && <span className={classes.spinner}></span>}
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          {error && (
            <div className={classes.errorMessage}>
              {error}
            </div>
          )}
        </form>
      </motion.div>
    </div>
  );
};

export default AdminLogin;